import { useCallback, useEffect, useRef, useState } from "react";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";

/** Long enough for the BE's first typing frame (~4 s heartbeat worst case), short enough not to lie. */
export const PENDING_REPLY_TIMEOUT_MS = 8000;

/** Whether the AI will answer a visitor message now: the agent is not switched off, no operator has the
 *  conversation (`connected`), nor is it waiting for one (`pending`: the AI stays silent then), and the
 *  send is not just queued offline (`online: false`: nothing reached the BE yet). */
export function shouldAwaitAiReply(args: {
  aiEnabled: boolean | undefined;
  assignmentStatus: "connected" | "pending" | null | undefined;
  online?: boolean;
}): boolean {
  return args.aiEnabled !== false && !args.assignmentStatus && args.online !== false;
}

const NON_MESSAGE_KINDS = new Set(["read-receipt", "status", "blocked", "reaction"]);

/** Whether a realtime event ends the local "thinking" bubble: the real (non-visitor) typing frame replaces
 *  it, an assignment change makes the guess stale, and any real non-visitor message is the reply itself.
 *  The visitor's own typing or message echo never clears it. */
export function clearsPendingReply(event: RealtimeMessageEvent): boolean {
  if (event.kind === "assignment") return true;
  if (NON_MESSAGE_KINDS.has(event.kind) || event.update || event.deleted) return false;
  return (event.from ?? event.sender)?.kind !== "visitor";
}

/**
 * A local "the AI is thinking" flag for the open conversation: armed by the visitor's send (so the
 * bubble shows within a frame, before any realtime frame can arrive), cleared by `clear()` or after
 * 8 s. Never announced and never stored: it is view state only.
 */
export function usePendingReply(eligible: boolean): {
  pending: boolean;
  arm: () => void;
  clear: () => void;
} {
  const [pending, setPending] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eligibleRef = useRef(eligible);
  eligibleRef.current = eligible;

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPending(false);
  }, []);
  const arm = useCallback(() => {
    if (!eligibleRef.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(clear, PENDING_REPLY_TIMEOUT_MS);
    setPending(true);
  }, [clear]);

  // An operator taking over (or the AI being switched off) mid-wait makes the guess wrong.
  useEffect(() => {
    if (!eligible) clear();
  }, [eligible, clear]);
  useEffect(() => clear, [clear]);

  return { pending, arm, clear };
}
